import { FC, useState, useEffect } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

import { buildClientEntity } from 'utils/helpers/client';
import { useActiveClientContext } from 'store/active-client';

import { useQuery, useQueryClient } from 'react-query';
import API from 'core/Api';
import Loader from 'components/Loader';
import { useLanguageContext } from 'store/language';
import { useTranslation } from 'react-i18next';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import enGB from 'date-fns/locale/en-GB';
import bg from 'date-fns/locale/bg';
import styled from 'styled-components';
import { calendarLegendItemsColleague, calendarLegendHomeOffice } from 'utils/helpers/leaves';
import TooltipWrapper from 'components/TooltipWrapper';
import CustomEventWrapper from './CustomEventWrapper';

import 'react-big-calendar/lib/css/react-big-calendar.css';

export interface ILeave {
  [key: string]: string | number | any;
  id: number;
  name: string;
  schedule: number[] | string[];
  status: string;
}

interface ScreenProps {
  showAttendant?: boolean;
}

const Screen: FC<ScreenProps> = ({ showAttendant }) => {
  const { t } = useTranslation();
  const { client } = useActiveClientContext();
  const { language } = useLanguageContext();

  const queryClient = useQueryClient();

  const messages = {
    allDay: t('leaves.employer.calendar.allDay'),
    previous: t('leaves.employer.calendar.previous'),
    next: t('leaves.employer.calendar.next'),
    today: t('leaves.employer.calendar.today'),
    month: t('leaves.employer.calendar.month'),
    week: t('leaves.employer.calendar.week'),
    day: t('leaves.employer.calendar.day'),
    agenda: t('leaves.employer.calendar.agenda'),
    date: t('leaves.employer.calendar.date'),
    time: t('leaves.employer.calendar.time'),
    event: t('leaves.employer.calendar.event'),
    showMore: (total: number) => t('leaves.employer.calendar.showMore', { total }),
  };

  const locales = { bg, en: enGB };

  const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
  });

  const [currentRange, setCurrentRange] = useState({
    date_from: format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 'yyyy-MM-dd'),
    date_to: format(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0), 'yyyy-MM-dd'),
  });

  const updateRange = (date: Date) => {
    const start = new Date(date.getFullYear(), date.getMonth(), 1);
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    setCurrentRange({
      date_from: format(start, 'yyyy-MM-dd'),
      date_to: format(end, 'yyyy-MM-dd'),
    });
  };

  const {
    isLoading,
    error,
    data: calendarEventsData = [],
  } = useQuery<ILeave[], Error>(
    ['leaves', client, currentRange.date_from, currentRange.date_to, showAttendant],
    (): Promise<ILeave[]> => {
      return API.get(
        `leaves/colleagues/calendar?limit=1000&offset=0&date_from=${currentRange.date_from}&date_to=${currentRange.date_to}&show_attendant=${showAttendant}`
      ).then(res =>
        res.data?.items?.map((leave: any) => ({
          ...leave,
          start: new Date(leave.date_from).setHours(0, 0, 0, 0),
          end: new Date(leave.date_to).setHours(23, 59, 59, 999),
        }))
      );
    }
  );

  const {
    isLoading: isLoadingHolidays,
    error: errorHolidays,
    data: holidaysData = [],
  } = useQuery<ILeave[], Error>(
    ['holidays', client, currentRange.date_from.split('-')[0]],
    (): Promise<ILeave[]> => {
      const year = currentRange.date_from.split('-')[0];
      const cachedData = queryClient.getQueryData<ILeave[]>(['holidays', client, year]);

      if (cachedData) {
        // If data for the year is already cached, return it
        return Promise.resolve(cachedData);
      }

      return API.get(`holidays/${year}`).then(res =>
        res.data?.items?.map((holiday: any, index: number) => {
          const start = new Date(holiday.date);
          start.setHours(0, 0, 0, 0);
          const end = new Date(holiday.date);
          end.setHours(23, 59, 59, 999);

          return {
            ...holiday,
            id: index,
            title: holiday.name,
            start,
            end,
            type: 'holiday',
            allDay: true,
          };
        })
      );
    },
    {
      keepPreviousData: true,
      enabled: !!currentRange.date_from,
    }
  );

  const CustomToolbar = (toolbar: any) => {
    const goToBack = () => {
      toolbar.date.setMonth(toolbar.date.getMonth() - 1);
      toolbar.onNavigate('prev');
      updateRange(toolbar.date);
    };

    const goToNext = () => {
      toolbar.date.setMonth(toolbar.date.getMonth() + 1);
      toolbar.onNavigate('next');
      updateRange(toolbar.date);
    };

    return (
      <StyledCustomToolbar>
        <button
          type="button"
          onClick={goToBack}
        >
          <FaChevronLeft size={16} />
        </button>
        <h5 className="text-center">
          {t(`leaves.months.${toolbar.date.getMonth() + 1}`)} {toolbar.date.getFullYear()}
        </h5>
        <button
          type="button"
          onClick={goToNext}
        >
          <FaChevronRight size={16} />
        </button>
      </StyledCustomToolbar>
    );
  };

  const processedEvents = [...holidaysData, ...calendarEventsData]
    .map(event => ({
      ...event,
      start: new Date(event.start),
      end: new Date(event.end),
    }))
    .filter(event => event.status !== 'rejected');

  const legendItems = showAttendant ? calendarLegendHomeOffice : calendarLegendItemsColleague;
  const tooltipContent = showAttendant ? t('leaves.legend.tooltip') : t('leaves.legend.tooltip');

  return (
    <StyledCalendar>
      <div className="d-flex">
        <TooltipWrapper
          id="tooltip-legend"
          overlay={tooltipContent}
        >
          <CalendarLegend>
            {legendItems.map(item => (
              <LegendItem
                key={item.type}
                style={{
                  backgroundColor: `${item.backgroundColor}`,
                  color: item.color || '#fff',
                }}
              >
                {t(`leaves.legend.${item.type}`)}
              </LegendItem>
            ))}
          </CalendarLegend>
        </TooltipWrapper>
      </div>
      {!isLoading && !isLoadingHolidays ? (
        <Calendar
          date={new Date(currentRange.date_from)}
          events={processedEvents}
          localizer={localizer}
          culture={language || 'bg'}
          views={{ month: true }}
          components={{ eventWrapper: CustomEventWrapper, toolbar: CustomToolbar }}
          messages={messages}
          popup
          style={{ height: '650px' }}
        />
      ) : (
        <LoaderWrapper>
          <Loader size="large" />
        </LoaderWrapper>
      )}
    </StyledCalendar>
  );
};

export default Screen;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100%;
`;

const StyledCalendar = styled.div`
  .rbc-calendar {
    .rbc-month-view {
      min-width: 35rem;
    }
  }
`;

const StyledCustomToolbar = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 16px;

  h5 {
    margin: 0;
    min-width: 360px;
  }

  button {
    border: 0;
    background: none;
    opacity: 0.4;
    transition: 0.3s;

    svg {
      fill: #575962;
    }

    &:hover {
      opacity: 1;
      transition: 0.3s;
    }
  }
`;

const CalendarLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;

const LegendItem = styled.div`
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
`;
